/**
 * What the pre-auth pages draw around the sign-in button, derived from the
 * tenant's `loginPage` options.
 *
 * Re-validated on read even though the server already normalised the values:
 * these become real `href`s on a public page, and a `loginPage` column can be
 * written by hand. The rule mirrors `splash.ts` — only https URLs and plain
 * addresses pass; anything else reads as "not set".
 *
 * Labels are NOT produced here: a link carries its `key`, and the component
 * translates it (`preauth.<key>` in src/i18n) — the tenant's own strings
 * (`signInLabel`) are the only untranslated copy on the page.
 */
import type { AppLoginPage } from "./types";
import { VENDOR_NAME } from "./vendor";

const MAX_SIGN_IN_LABEL_CHARS = 40;
/** Same shape rules as the backend's urls.ts: https only, no quotes or
 *  angle brackets, a 2048 cap; an address with no `?&%#/` so it can never
 *  carry a `mailto:` query. */
const MAX_OPTION_URL_CHARS = 2048;
const HTTPS_URL = /^https:\/\/[^\s/$.?#"'<>][^\s"'<>]*$/i;
const EMAIL = /^[^\s@?&%#/"'<>]+@[^\s@?&%#/"'<>]+\.[^\s@?&%#/"'<>]+$/;

export type PreAuthLinkKey = "support" | "terms" | "privacy";

export interface PreAuthLink {
  key: PreAuthLinkKey;
  href: string;
}

function httpsOrNull(value: string | null | undefined): string | null {
  const url = value?.trim() ?? "";
  return url.length <= MAX_OPTION_URL_CHARS && HTTPS_URL.test(url) ? url : null;
}

/** The app's configured sign-in button text, or null for the shell's own translated default. */
export function signInLabel(loginPage: AppLoginPage | null | undefined): string | null {
  const label = loginPage?.signInLabel?.trim() ?? "";
  return label ? label.slice(0, MAX_SIGN_IN_LABEL_CHARS) : null;
}

/** "Need help?" destination: the URL wins over the email; null when neither is usable. */
export function supportHref(loginPage: AppLoginPage | null | undefined): string | null {
  const url = httpsOrNull(loginPage?.supportUrl);
  if (url) return url;
  const email = loginPage?.supportEmail?.trim() ?? "";
  return email.length <= MAX_OPTION_URL_CHARS && EMAIL.test(email) ? `mailto:${email}` : null;
}

/** Footer links in display order — support first (the one a stuck visitor needs), then legal. */
export function preAuthLinks(loginPage: AppLoginPage | null | undefined): PreAuthLink[] {
  const links: PreAuthLink[] = [];
  const support = supportHref(loginPage);
  if (support) links.push({ key: "support", href: support });
  const terms = httpsOrNull(loginPage?.termsUrl);
  if (terms) links.push({ key: "terms", href: terms });
  const privacy = httpsOrNull(loginPage?.privacyUrl);
  if (privacy) links.push({ key: "privacy", href: privacy });
  return links;
}

/**
 * The vendor credit. Off when the admin switched it off, and never on an app
 * literally called by the vendor's name — our own front door should not
 * advertise us twice.
 */
export function showPoweredBy(
  loginPage: AppLoginPage | null | undefined,
  appName: string,
  vendorName: string = VENDOR_NAME,
): boolean {
  if (loginPage?.poweredBy === false) return false;
  return appName.trim().toLowerCase() !== vendorName.trim().toLowerCase();
}
